import React from 'react';

import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { UseMutationResult, useQueryClient } from 'react-query';
import { AthletePackageRetrieve, ErrorMessage } from 'types';
import notificationHandler from 'utils/notifications';

interface IUndoCancelModalProps {
  opened: boolean;
  athletePackage: AthletePackageRetrieve;
  closeHandler: () => void;
  undoCancelAthletePackageMutation: UseMutationResult<AxiosResponse<null>, AxiosError<ErrorMessage>, string>;
}

const UndoCancelModal = ({
  opened,
  athletePackage,
  closeHandler,
  undoCancelAthletePackageMutation,
}: IUndoCancelModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const queryClient = useQueryClient();

  const handleUndoCancel = (): void => {
    undoCancelAthletePackageMutation.mutate(athletePackage.athletePackageId, {
      onError: error => {
        errorNotification('Undo cancel failed', error.response?.data.message);
        closeHandler();
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['invoices']);
        queryClient.invalidateQueries(['athleteEvents']);
        successNotification('Undo cancel success');
        closeHandler();
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={closeHandler}
      size='auto'
      centered
      title={<Title order={4}>Undo Future Cancellation</Title>}
    >
      <Stack p='md'>
        <Stack spacing={0}>
          <Text color='dimmed' size='sm'>
            This package is set to cancel early on{' '}
            {dayjs(athletePackage.cancellationDate).format('YYYY/MM/DD')}.
          </Text>
          <Text color='dimmed' size='sm'>
            Undoing this scheduled cancellation will un-void upcoming invoices and the package will end on
            its original end date.
          </Text>
        </Stack>
        <Text color='dimmed' size='sm' ta='center'>
          Are you sure you want to continue?
        </Text>
      </Stack>
      <Group position='right' pt='md'>
        <Button
          variant='light'
          onClick={closeHandler}
          size='xs'
          radius='md'
          disabled={undoCancelAthletePackageMutation.isLoading}
        >
          Cancel
        </Button>
        <Button
          size='xs'
          radius='md'
          color='red'
          loading={undoCancelAthletePackageMutation.isLoading}
          onClick={handleUndoCancel}
        >
          Undo
        </Button>
      </Group>
    </Modal>
  );
};

export default UndoCancelModal;
